import { PlusOutlined } from '@ant-design/icons';
import { useCustom, useTranslate } from '@refinedev/core';
import { Alert, Button, Space, Spin, Table, Tag, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TEMPLATE_DETAIL_FIELDS,
  TEMPLATE_ROW_FIELDS,
  draftFromDetail,
  emptyDraft,
  type TemplateDetail,
  type TemplateRow,
} from '../worksheets/model';
import { ALL_CATEGORIES, filterByCategory } from '../worksheets/categoryFilter';
import { WorksheetBuilder } from '../worksheets/WorksheetBuilder';
import { WorksheetCategorySelect } from '../worksheets/WorksheetCategorySelect';

/**
 * E34 · PublishWorksheetTemplate — "Backoffice — Worksheet library — Publish" (cc-homework, chapter
 * "Worksheets — Templates & library (portal, E34)", screen e34e0009-0001-4000-8000-000000000001): the
 * CopingCard starter library (EN + RU — drafted with AI, reviewed by Dmitri before publication) is
 * published from the backoffice (E33) with the same Builder the practices use; a practice copies a
 * library template into its own ("Use this template"). The member names the template (`lib-…`); a known
 * id publishes its next version. Behavior: gesture Publish → PublishWorksheetTemplate, i18n
 * backoffice.worksheets.*. The page sits inside the backoffice shell (members only — the server's gate).
 */
export const BackofficeWorksheets = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const { data, isFetching, isError } = useCustom<TemplateRow[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: { operation: 'worksheetTemplates', fields: TEMPLATE_ROW_FIELDS },
  });
  const rows = useMemo(
    () => (data?.data ?? []).filter((r) => r.owner === 'library'),
    [data],
  );
  // P039: the same Category filter as the pickers — opens on All, counts from the whole library list
  const [category, setCategory] = useState<string>(ALL_CATEGORIES);
  const shown = useMemo(() => filterByCategory(rows, category), [rows, category]);
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }} data-testid="backoffice-worksheets">
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <div>
          <Typography.Title level={3} style={{ marginBottom: 0 }}>
            {t('backoffice.worksheets.title')}
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('backoffice.worksheets.intro')}
          </Typography.Paragraph>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/backoffice/worksheets/new')}
          data-testid="backoffice-worksheets-new"
        >
          {t('backoffice.worksheets.new')}
        </Button>
      </Space>
      {isError ? <Alert type="error" showIcon message={t('worksheets.library.loadFailed')} /> : null}
      <WorksheetCategorySelect
        templates={rows}
        value={category}
        onChange={setCategory}
        style={{ maxWidth: 360 }}
        data-testid="backoffice-worksheets-category"
      />
      <Table<TemplateRow>
        rowKey="templateId"
        loading={isFetching}
        dataSource={shown}
        pagination={{ pageSize: 25, hideOnSinglePage: true }}
        locale={{ emptyText: t('backoffice.worksheets.empty') }}
        onRow={(r) => ({ 'data-testid': `backoffice-worksheet-row-${r.templateId}` } as never)}
        columns={[
          { title: t('worksheets.library.name'), dataIndex: 'name' },
          { title: 'ID', dataIndex: 'templateId' },
          {
            title: t('worksheets.library.category'),
            dataIndex: 'category',
            render: (c: string) => <Tag>{t(`worksheets.category.${c}`)}</Tag>,
          },
          {
            title: t('worksheets.library.language'),
            dataIndex: 'language',
            render: (l: string) => t(`worksheets.language.${l}`),
          },
          {
            title: t('backoffice.worksheets.version'),
            dataIndex: 'version',
          },
          {
            title: '',
            key: 'actions',
            render: (_: unknown, r) => (
              <Button
                onClick={() => navigate(`/backoffice/worksheets/${encodeURIComponent(r.templateId)}/edit`)}
                data-testid={`backoffice-worksheet-edit-${r.templateId}`}
              >
                {t('backoffice.worksheets.newVersion')}
              </Button>
            ),
          },
        ]}
      />
    </Space>
  );
};

/** `/backoffice/worksheets/new` and `/backoffice/worksheets/<lib-id>/edit` — the Builder in publish mode. */
export const BackofficeWorksheetPublish = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { data, isLoading, isError } = useCustom<TemplateDetail>({
    url: '',
    method: 'get',
    errorNotification: false,
    queryOptions: { enabled: Boolean(templateId) },
    meta: {
      operation: 'worksheetTemplate',
      fields: TEMPLATE_DETAIL_FIELDS,
      variables: { templateId: { name: 'templateId', type: 'ID!', value: templateId } },
    },
  });
  if (templateId && isLoading) return <Spin />;
  const detail = data?.data ?? null;
  if (templateId && (isError || !detail || detail.owner !== 'library')) {
    return <Alert type="error" showIcon message={t('worksheets.builder.notFound')} />;
  }
  const leave = () => navigate('/backoffice/worksheets');
  return (
    <WorksheetBuilder
      key={templateId ?? 'new'}
      mode="publish"
      initial={detail ? draftFromDetail(detail, 'edit') : emptyDraft('en')}
      templateId={templateId ?? null}
      onSaved={leave}
      onCancel={leave}
      heading={
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t(templateId ? 'backoffice.worksheets.titleVersion' : 'backoffice.worksheets.titleNew')}
        </Typography.Title>
      }
    />
  );
};
